import { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

type SignedUrlState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error" };

const urlCache = new Map<string, { url: string; expiresAtMs: number }>();
const inFlight = new Map<string, Promise<string | null>>();

function nowMs() {
  return Date.now();
}

async function getSignedUrl(bucket: string, path: string, expiresInSeconds: number): Promise<string | null> {
  const cached = urlCache.get(path);
  if (cached && cached.expiresAtMs > nowMs() + 30_000) return cached.url; // refresh a bit early

  const existing = inFlight.get(path);
  if (existing) return existing;

  const p = (async () => {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresInSeconds);
    if (error || !data?.signedUrl) return null;
    urlCache.set(path, { url: data.signedUrl, expiresAtMs: nowMs() + expiresInSeconds * 1000 });
    return data.signedUrl;
  })();

  inFlight.set(path, p);
  try {
    return await p;
  } finally {
    inFlight.delete(path);
  }
}

export function useSignedObjectUrl({
  bucket,
  path,
  expiresInSeconds = 60 * 60,
}: {
  bucket: string;
  path?: string;
  expiresInSeconds?: number;
}) {
  const key = useMemo(() => (path ? `${bucket}:${path}` : null), [bucket, path]);
  const [state, setState] = useState<SignedUrlState>({ status: "idle" });

  useEffect(() => {
    let cancelled = false;
    if (!path) {
      setState({ status: "idle" });
      return;
    }

    setState({ status: "loading" });
    getSignedUrl(bucket, path, expiresInSeconds)
      .then((url) => {
        if (cancelled) return;
        if (!url) setState({ status: "error" });
        else setState({ status: "ready", url });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [bucket, expiresInSeconds, key, path]);

  return state;
}

